// Updated ModuleDetail.jsx - Fix material upload refresh and loading issues
import React, { useState, useRef, useEffect } from 'react';
import {
    ArrowLeft, Plus, Edit, Trash2, Video, FileText, File,
    PlayCircle, Eye, CheckCircle, Calendar, Download,
    Upload, X, Clock
} from 'lucide-react';
import apiService from '../../services/api';

const ModuleDetail = ({ module, chapters, onBack, onRefresh, setError, setSuccess }) => {
    const [showChapterModal, setShowChapterModal] = useState(false);
    const [showFileUpload, setShowFileUpload] = useState(false);
    const [editingChapter, setEditingChapter] = useState(null);
    const [selectedChapterForUpload, setSelectedChapterForUpload] = useState(null);
    const [chapterForm, setChapterForm] = useState({
        title: '',
        description: '',
        duration: ''
    });

    // File upload states
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [videoThumbnail, setVideoThumbnail] = useState(null);
    const fileInputRef = useRef(null);

    // Material management states
    const [chapterMaterials, setChapterMaterials] = useState({});
    const [materialsLoading, setMaterialsLoading] = useState({});

    // Fetch materials for each chapter
    useEffect(() => {
        if (chapters && chapters.length > 0) {
            chapters.forEach(chapter => {
                fetchChapterMaterials(chapter.id);
            });
        }
    }, [chapters]);

    const fetchChapterMaterials = async (chapterId) => {
        try {
            setMaterialsLoading(prev => ({ ...prev, [chapterId]: true }));
            const materials = await apiService.getChapterMaterials(chapterId);
            setChapterMaterials(prev => ({ ...prev, [chapterId]: materials }));
        } catch (error) {
            console.error('Error fetching materials:', error);
            // Don't show error for missing materials, just set empty array
            setChapterMaterials(prev => ({ ...prev, [chapterId]: [] }));
        } finally {
            setMaterialsLoading(prev => ({ ...prev, [chapterId]: false }));
        }
    };

    // Enhanced FileTypeIcon component with debugging
    const FileTypeIcon = ({ filename, size = "w-5 h-5" }) => {
        const getFileExtension = (filename) => {
            if (!filename) return '';
            const parts = filename.split('.');
            if (parts.length < 2) return '';
            return parts.pop().toLowerCase();
        };

        const getFileIconPath = (extension) => {
            const iconMap = {
                // Document files
                'pdf': 'pdf.png',
                'doc': 'word.png',
                'docx': 'word.png',
                'txt': 'txt.png',
                'rtf': 'word.png',

                // Spreadsheet files
                'xls': 'excel.png',
                'xlsx': 'excel.png',
                'csv': 'excel.png',

                // Presentation files
                'ppt': 'powerpoint.png',
                'pptx': 'powerpoint.png',

                // Image files
                'jpg': 'image.png',
                'jpeg': 'image.png',
                'png': 'image.png',
                'gif': 'image.png',
                'svg': 'image.png',
                'bmp': 'image.png',
                'webp': 'image.png',

                // Video files
                'mp4': 'video.png',
                'avi': 'video.png',
                'mov': 'video.png',
                'wmv': 'video.png',
                'webm': 'video.png',
                'mkv': 'video.png',

                // Audio files
                'mp3': 'audio.png',
                'wav': 'audio.png',
                'flac': 'audio.png',
                'aac': 'audio.png',
                'ogg': 'audio.png',

                // Archive files
                'zip': 'zip.png',
                'rar': 'zip.png',
                '7z': 'zip.png',
                'tar': 'zip.png',
                'gz': 'zip.png',

                // Other files
                'json': 'code.png',
                'xml': 'code.png',
                'html': 'code.png',
                'css': 'code.png',
                'js': 'code.png',
            };

            return iconMap[extension] || 'file.png';
        };

        const getFileTypeName = (extension) => {
            const nameMap = {
                'pdf': 'PDF Document',
                'doc': 'Word Document',
                'docx': 'Word Document',
                'txt': 'Text File',
                'xls': 'Excel Spreadsheet',
                'xlsx': 'Excel Spreadsheet',
                'csv': 'CSV File',
                'ppt': 'PowerPoint Presentation',
                'pptx': 'PowerPoint Presentation',
                'jpg': 'JPEG Image',
                'jpeg': 'JPEG Image',
                'png': 'PNG Image',
                'gif': 'GIF Image',
                'mp4': 'MP4 Video',
                'avi': 'AVI Video',
                'mov': 'QuickTime Video',
                'mp3': 'MP3 Audio',
                'wav': 'WAV Audio',
                'zip': 'ZIP Archive',
                'rar': 'RAR Archive',
            };

            return nameMap[extension] || 'File';
        };

        if (!filename) {
            return <File className={`${size} text-gray-400`} />;
        }

        const extension = getFileExtension(filename);
        const iconPath = getFileIconPath(extension);
        const fileTypeName = getFileTypeName(extension);

        return (
            <div className="flex items-center justify-center">
                <img
                    src={`/icons/${iconPath}`}
                    alt={`${fileTypeName} icon`}
                    className={`${size} object-contain`}
                    onError={(e) => {
                        e.target.src = '/icons/file.png';
                    }}
                    title={fileTypeName}
                />
            </div>
        );
    };

    // Enhanced Video Thumbnail Component
    const VideoThumbnail = ({ chapter }) => {
        const videoRef = useRef(null);
        const canvasRef = useRef(null);
        const [thumbnail, setThumbnail] = useState(null);
        const [thumbnailLoading, setThumbnailLoading] = useState(false);
        const [videoDuration, setVideoDuration] = useState(null);
        const [thumbnailError, setThumbnailError] = useState(false);
        const [durationLoading, setDurationLoading] = useState(false);

        useEffect(() => {
            if (chapter.video_url) {
                generateThumbnailAndDuration();
            }
        }, [chapter.video_url]);

        const formatDuration = (seconds) => {
            if (!seconds || isNaN(seconds)) return null;

            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = Math.floor(seconds % 60);

            if (minutes > 0) {
                return remainingSeconds > 0 ? `${minutes}min ${remainingSeconds}seg` : `${minutes}min`;
            }
            return `${remainingSeconds}seg`;
        };

        const generateThumbnailAndDuration = async () => {
            if (!chapter.video_url) return;

            setThumbnailLoading(true);
            setDurationLoading(true);
            setThumbnailError(false);

            try {
                const video = videoRef.current;
                const canvas = canvasRef.current;

                if (!video || !canvas) return;

                const videoUrl = apiService.getVideoUrl(chapter.video_url);
                video.src = videoUrl;
                video.crossOrigin = 'anonymous';
                video.preload = 'metadata';

                video.onloadedmetadata = () => {
                    try {
                        const duration = video.duration;
                        if (duration && !isNaN(duration)) {
                            const formattedDuration = formatDuration(duration);
                            setVideoDuration(formattedDuration);

                            if (formattedDuration && chapter.duration !== formattedDuration) {
                                chapter.calculatedDuration = formattedDuration;
                                chapter.durationSeconds = duration;
                            }
                        }
                        setDurationLoading(false);

                        const seekTime = Math.min(duration * 0.1, 10);
                        video.currentTime = seekTime;
                    } catch (error) {
                        console.warn('Could not process video metadata:', error);
                        setDurationLoading(false);
                        video.currentTime = 0;
                    }
                };

                video.onseeked = () => {
                    try {
                        const ctx = canvas.getContext('2d');
                        canvas.width = 320;
                        canvas.height = 180;

                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                        const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
                        setThumbnail(thumbnailUrl);
                        setThumbnailLoading(false);
                    } catch (error) {
                        console.error('Error generating thumbnail:', error);
                        setThumbnailError(true);
                        setThumbnailLoading(false);
                    }
                };

                video.onerror = () => {
                    console.error('Error loading video for thumbnail');
                    setThumbnailError(true);
                    setThumbnailLoading(false);
                    setDurationLoading(false);
                };

            } catch (error) {
                console.error('Error setting up video processing:', error);
                setThumbnailError(true);
                setThumbnailLoading(false);
                setDurationLoading(false);
            }
        };

        if (!chapter.video_url) {
            return (
                <div className="relative w-40 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                        <Video className="w-8 h-8 text-gray-400" />
                        <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded">
                            Sin video
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="relative w-40 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                <video
                    ref={videoRef}
                    style={{ display: 'none' }}
                    muted
                    preload="metadata"
                />
                <canvas
                    ref={canvasRef}
                    style={{ display: 'none' }}
                />

                {thumbnailLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mb-1"></div>
                            <p className="text-xs text-gray-600">Cargando...</p>
                        </div>
                    </div>
                )}

                {thumbnailError && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                            <Video className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                            <p className="text-xs text-gray-500">Vista previa no disponible</p>
                        </div>
                    </div>
                )}

                {thumbnail ? (
                    <>
                        <img
                            src={thumbnail}
                            alt={`Vista previa de ${chapter.title}`}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                            <PlayCircle className="w-8 h-8 text-white" />
                        </div>
                        <div className="absolute bottom-1 right-1 bg-black bg-opacity-75 text-white px-1 py-0.5 rounded text-xs">
                            {durationLoading ? (
                                <div className="flex items-center space-x-1">
                                    <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                                    <span>...</span>
                                </div>
                            ) : (
                                videoDuration || chapter.duration || 'N/A'
                            )}
                        </div>
                    </>
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
                        <Video className="w-8 h-8 text-purple-600" />
                        <div className="absolute bottom-1 left-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded text-center">
                            <div className="font-medium truncate max-w-[120px]">{chapter.video_url}</div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const handleCreateChapter = async (e) => {
        e.preventDefault();
        try {
            const chapterData = {
                ...chapterForm,
                module_id: module.id
            };

            if (editingChapter) {
                await apiService.updateChapter(editingChapter.id, chapterData);
                setSuccess('Capítulo actualizado exitosamente');
            } else {
                await apiService.createChapter(chapterData);
                setSuccess('Capítulo creado exitosamente');
            }
            setShowChapterModal(false);
            resetChapterForm();
            onRefresh();
        } catch (err) {
            setError(`Error al guardar capítulo: ${err.message}`);
        }
    };

    const handleDeleteMaterial = async (materialId, materialName, chapterId) => {
        if (!materialId) {
            setError('Error: ID del material no disponible');
            console.error('Material ID is undefined:', { materialId, materialName, chapterId });
            return;
        }
        
        if (!window.confirm(`¿Eliminar el material "${materialName}"?`)) return;
        
        try {
            await apiService.deleteMaterial(chapterId, materialId);  // FIX: Added chapterId
            setSuccess('Material eliminado exitosamente');
            await fetchChapterMaterials(chapterId);
        } catch (err) {
            setError(`Error al eliminar material: ${err.message}`);
        }
    };

    const handleDeleteChapter = async (chapterId, chapterTitle) => {
        if (!window.confirm(`¿Eliminar el capítulo "${chapterTitle}"?`)) return;

        try {
            await apiService.deleteChapter(chapterId);
            setSuccess('Capítulo eliminado exitosamente');
            onRefresh();
        } catch (err) {
            setError(`Error al eliminar capítulo: ${err.message}`);
        }
    };

    const handleFileUpload = async () => {
        if (!selectedFile || !selectedChapterForUpload) return;

        try {
            setUploadProgress(10);

            let uploadResponse;
            if (selectedFile.type.startsWith('video/')) {
                uploadResponse = await apiService.uploadVideo(
                    selectedChapterForUpload.id,
                    selectedFile,
                    (progress) => setUploadProgress(progress)
                );
                setSuccess(`Video subido exitosamente`);
                // Refresh chapters to update video info
                onRefresh();
            } else {
                // FIX: Use the correct method name and parameters
                uploadResponse = await apiService.uploadMaterial(
                    selectedChapterForUpload.id,
                    selectedFile,
                    {
                        title: selectedFile.name,
                        description: '',
                        onProgress: (progress) => setUploadProgress(progress)
                    }
                );
                setSuccess(`Material subido exitosamente`);
                // Refresh materials for this specific chapter
                await fetchChapterMaterials(selectedChapterForUpload.id);
            }

            setShowFileUpload(false);
            resetFileUpload();
        } catch (err) {
            setError(`Error al subir archivo: ${err.message}`);
            setUploadProgress(0);
        }
    };

    const handleDeleteVideo = async (chapterId) => {
        if (!window.confirm('¿Eliminar el video de este capítulo?')) return;

        try {
            await apiService.deleteVideo(chapterId);
            setSuccess('Video eliminado exitosamente');
            onRefresh();
        } catch (err) {
            setError(`Error al eliminar video: ${err.message}`);
        }
    };




    const downloadMaterial = async (material, chapterId) => {
        try {
            const downloadUrl = await apiService.getMaterialDownloadUrl(material.id);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = material.original_filename || material.filename || material.title;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            setError(`Error al descargar material: ${err.message}`);
        }
    };

    const openEditChapter = (chapter) => {
        setEditingChapter(chapter);
        setChapterForm({
            title: chapter.title,
            description: chapter.description || '',
            duration: chapter.duration || ''
        });
        setShowChapterModal(true);
    };

    const openFileUpload = (chapter) => {
        setSelectedChapterForUpload(chapter);
        setShowFileUpload(true);
    };

    const resetChapterForm = () => {
        setChapterForm({ title: '', description: '', duration: '' });
        setEditingChapter(null);
    };

    const resetFileUpload = () => {
        setSelectedFile(null);
        setUploadProgress(0);
        setVideoThumbnail(null);
        setSelectedChapterForUpload(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setSelectedFile(file);

        if (file.type.startsWith('video/')) {
            try {
                const thumbnail = await generateVideoThumbnail(file);
                setVideoThumbnail(thumbnail);
            } catch (error) {
                console.error('Error generating thumbnail:', error);
            }
        }
    };

    const generateVideoThumbnail = (file) => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            video.onloadedmetadata = () => {
                canvas.width = 160;
                canvas.height = 90;
                video.currentTime = 1;
            };

            video.onseeked = () => {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const thumbnail = canvas.toDataURL('image/jpeg', 0.8);
                resolve(thumbnail);
                URL.revokeObjectURL(video.src);
            };

            video.onerror = () => {
                reject(new Error('Error loading video'));
                URL.revokeObjectURL(video.src);
            };

            video.src = URL.createObjectURL(file);
            video.load();
        });
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'Tamaño desconocido';
        const mb = bytes / 1024 / 1024;
        return `${mb.toFixed(1)} MB`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Sin fecha';
        return new Date(dateString).toLocaleDateString('es-ES');
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center space-x-4 mb-6">
                <button
                    onClick={onBack}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex-1">
                    <h2 className="text-3xl font-bold text-gray-900">
                        {module.icon} {module.title}
                    </h2>
                    <p className="text-gray-600">{module.description}</p>
                </div>
                <button
                    onClick={() => setShowChapterModal(true)}
                    className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 flex items-center space-x-2 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    <span>Agregar Capítulo</span>
                </button>
            </div>

            {/* Module Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {[
                    { label: 'Capítulos', value: chapters.length, icon: FileText, color: 'blue' },
                    { label: 'Videos', value: chapters.filter(ch => ch.video_url).length, icon: Video, color: 'purple' }
                ].map((stat, index) => (
                    <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg bg-${stat.color}-100`}>
                                <stat.icon className={`w-5 h-5 text-${stat.color}-600`} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                                <p className="text-sm text-gray-600">{stat.label}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Chapters List */}
            <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900">Capítulos</h3>

                {chapters.length > 0 ? (
                    chapters.map(chapter => (
                        <div key={chapter.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
                            <div className="flex items-start space-x-4">
                                <VideoThumbnail chapter={chapter} />

                                <div className="flex-1">
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="text-lg font-semibold text-gray-900">{chapter.title}</h4>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => openEditChapter(chapter)}
                                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Editar capítulo"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => openFileUpload(chapter)}
                                                className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                title="Subir archivo"
                                            >
                                                <Upload className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteChapter(chapter.id, chapter.title)}
                                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Eliminar capítulo"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    <p className="text-gray-600 text-sm mb-3">{chapter.description || 'Sin descripción'}</p>

                                    {/* Materials Section */}
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <h5 className="text-sm font-medium text-gray-700">
                                                Materiales de apoyo ({chapterMaterials[chapter.id]?.length || 0})
                                            </h5>
                                            <button
                                                onClick={() => openFileUpload(chapter)}
                                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                                            >
                                                <Plus className="w-3 h-3" />
                                                <span>Agregar</span>
                                            </button>
                                        </div>

                                        {materialsLoading[chapter.id] ? (
                                            <div className="text-center py-2 text-gray-500">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                                            </div>
                                        ) : chapterMaterials[chapter.id] && chapterMaterials[chapter.id].length > 0 ? (
                                            <div className="grid grid-cols-1 gap-2">
                                                {chapterMaterials[chapter.id].map((material, index) => {
                                                    const possibleFilenames = [
                                                        material.original_filename,
                                                        material.filename,
                                                        material.title,
                                                        material.original_name,
                                                        material.file_name,
                                                        material.name
                                                    ];

                                                    const filename = possibleFilenames.find(name => name && name.includes('.')) || possibleFilenames[0];

                                                    return (
                                                        <div key={material.id || index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                                                            <FileTypeIcon
                                                                filename={filename}
                                                                size="w-5 h-5"
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                                    {material.title || material.original_filename || 'Archivo sin nombre'}
                                                                </p>
                                                                <div className="flex items-center space-x-2 text-xs text-gray-500">
                                                                    <span>{material.file_type?.toUpperCase() || 'Archivo'}</span>
                                                                    <span>•</span>
                                                                    <span>{formatFileSize(material.file_size)}</span>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() => downloadMaterial(material, chapter.id)}
                                                                className="p-1 text-gray-400 hover:text-blue-600"
                                                                title="Descargar material"
                                                            >
                                                                <Download className="w-3 h-3" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteMaterial(material.id, material.title || material.original_filename, chapter.id)}
                                                                className="p-1 text-gray-400 hover:text-red-600"
                                                                title="Eliminar material"
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                                                <File className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                                <p className="text-sm">No hay materiales</p>
                                                <button
                                                    onClick={() => openFileUpload(chapter)}
                                                    className="text-xs text-blue-600 hover:text-blue-800 mt-1"
                                                >
                                                    Agregar primer material
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                        <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay capítulos</h3>
                        <p className="text-gray-500 mb-6">Comienza agregando el primer capítulo a este módulo</p>
                        <button
                            onClick={() => setShowChapterModal(true)}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Agregar Primer Capítulo
                        </button>
                    </div>
                )}
            </div>

            {/* Chapter Modal */}
            {showChapterModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
                        <h3 className="text-xl font-semibold text-gray-900 mb-6">
                            {editingChapter ? 'Editar Capítulo' : 'Crear Nuevo Capítulo'}
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Título del Capítulo
                                </label>
                                <input
                                    type="text"
                                    value={chapterForm.title}
                                    onChange={(e) => setChapterForm({ ...chapterForm, title: e.target.value })}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Descripción
                                </label>
                                <textarea
                                    value={chapterForm.description}
                                    onChange={(e) => setChapterForm({ ...chapterForm, description: e.target.value })}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    rows="3"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Duración estimada
                                </label>
                                <input
                                    type="text"
                                    value={chapterForm.duration}
                                    onChange={(e) => setChapterForm({ ...chapterForm, duration: e.target.value })}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Ej: 15:30"
                                />
                            </div>

                            <div className="flex space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowChapterModal(false);
                                        resetChapterForm();
                                    }}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCreateChapter}
                                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    {editingChapter ? 'Actualizar' : 'Crear Capítulo'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* File Upload Modal */}
            {showFileUpload && selectedChapterForUpload && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-semibold">
                                Subir Material - {selectedChapterForUpload.title}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowFileUpload(false);
                                    resetFileUpload();
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* File Drop Zone */}
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                                {!selectedFile ? (
                                    <div>
                                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <p className="text-lg text-gray-600 mb-2">Arrastra archivos aquí o haz clic para seleccionar</p>
                                        <p className="text-sm text-gray-500">Videos (MP4, AVI, MOV) y materiales (PDF, DOCX, etc.)</p>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            onChange={handleFileSelect}
                                            className="hidden"
                                            accept="video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.zip,.rar"
                                        />
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                        >
                                            Seleccionar Archivo
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {/* Video Thumbnail Preview */}
                                        {videoThumbnail && (
                                            <div className="w-48 h-28 mx-auto rounded-lg overflow-hidden border">
                                                <img src={videoThumbnail} alt="Video preview" className="w-full h-full object-cover" />
                                            </div>
                                        )}

                                        {/* File Info */}
                                        <div className="flex items-center justify-center space-x-3 p-4 bg-gray-50 rounded-lg">
                                            <FileTypeIcon filename={selectedFile.name} size="w-8 h-8" />
                                            <div>
                                                <p className="font-medium text-gray-900">{selectedFile.name}</p>
                                                <p className="text-sm text-gray-500">
                                                    {formatFileSize(selectedFile.size)} • {selectedFile.type}
                                                </p>
                                                <p className="text-xs text-blue-600">
                                                    {selectedFile.type.startsWith('video/') ? 'Se subirá como video' : 'Se subirá como material de apoyo'}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Upload Progress */}
                                        {uploadProgress > 0 && uploadProgress < 100 && (
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${uploadProgress}%` }}
                                                />
                                            </div>
                                        )}

                                        <div className="flex space-x-3">
                                            <button
                                                onClick={handleFileUpload}
                                                disabled={uploadProgress > 0 && uploadProgress < 100}
                                                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                                            >
                                                {uploadProgress > 0 && uploadProgress < 100 ? 'Subiendo...' : 'Subir Archivo'}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSelectedFile(null);
                                                    setVideoThumbnail(null);
                                                    setUploadProgress(0);
                                                }}
                                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* File Type Guidelines */}
                            <div className="bg-blue-50 rounded-lg p-4">
                                <h4 className="font-medium text-blue-900 mb-2">Tipos de archivo soportados:</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
                                    <div className="flex items-center space-x-2">
                                        <Video className="w-4 h-4" />
                                        <span>Videos: MP4, AVI, MOV, WMV</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <FileText className="w-4 h-4" />
                                        <span>Documentos: PDF, DOCX, XLSX</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <File className="w-4 h-4" />
                                        <span>Imágenes: JPG, PNG, GIF</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <File className="w-4 h-4" />
                                        <span>Archivos: ZIP, RAR</span>
                                    </div>
                                </div>
                                <p className="text-xs text-blue-700 mt-2">
                                    Los videos reemplazarán el video actual del capítulo. Otros archivos se agregarán como materiales de apoyo.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModuleDetail;